<template>
  <v2-modal
    :show="show"
    title="Import direct retailers"
    size="lg"
    @close="onClose"
  >
    <div class="v2-import-modal">
      <!-- Export section -->
      <div class="v2-flex v2-items-center v2-gap-3 v2-mb-4">
        <span class="v2-text-sm">Export direct retailers</span>
        <v2-button
          size="sm"
          :disabled="isExporting"
          :loading="isExporting"
          @click="$emit('export')"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line
              x1="12"
              y1="15"
              x2="12"
              y2="3"
            />
          </svg>
          {{ isExporting ? 'Downloading' : 'Download' }}
        </v2-button>
      </div>

      <!-- File upload area -->
      <label
        v-if="!importFile"
        for="v2UploadCatalog"
        class="v2-import-modal__dropzone"
      >
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="v2-import-modal__upload-icon"
        >
          <polyline points="16 16 12 12 8 16" />
          <line
            x1="12"
            y1="12"
            x2="12"
            y2="21"
          />
          <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
          <polyline points="16 16 12 12 8 16" />
        </svg>
        <p class="v2-font-semibold v2-mt-2">
          Drag & Drop
        </p>
        <p class="v2-text-sm v2-text-muted">
          or click to select files from your system
        </p>
      </label>

      <!-- File selected -->
      <div
        v-else
        class="v2-import-modal__file"
      >
        <div class="v2-flex v2-items-center v2-gap-3">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="v2-import-modal__file-icon"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <div>
            <p class="v2-text-sm v2-font-medium mb-0">
              {{ importFile.name }}
            </p>
            <p class="v2-text-xs v2-text-muted mb-0">
              {{ formatFileSize(importFile.size) }}
            </p>
          </div>
        </div>
        <button
          class="v2-import-modal__remove-btn"
          @click="removeFile"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      </div>

      <input
        id="v2UploadCatalog"
        type="file"
        accept=".xlsx"
        hidden
        @change="onFileSelect"
      >
    </div>

    <template #footer>
      <v2-button
        variant="outline"
        @click="onClose"
      >
        Cancel
      </v2-button>
      <v2-button
        :disabled="isUploading || !importFile"
        :loading="isUploading"
        @click="$emit('upload')"
      >
        {{ isUploading ? 'Saving' : 'Save' }}
      </v2-button>
    </template>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'

export default {
  name: 'V2ImportModal',
  components: {
    V2Modal,
    V2Button,
  },
  props: {
    show: { type: Boolean, default: false },
    importFile: { type: [File, Object], default: null },
    isExporting: { type: Boolean, default: false },
    isUploading: { type: Boolean, default: false },
  },
  methods: {
    onFileSelect(e) {
      const file = e.target.files?.[0]
      if (file) {
        this.$emit('file-selected', file)
      }
      e.target.value = ''
    },
    removeFile() {
      this.$emit('file-selected', null)
    },
    onClose() {
      this.$emit('close')
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 KB'
      return `${(bytes / 1024).toFixed(2)} KB`
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-import-modal {
  &__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-8);
    border: 2px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;
    text-align: center;

    &:hover {
      border-color: var(--v2-primary);
      background: var(--v2-muted);
    }
  }

  &__upload-icon {
    color: var(--v2-primary);
  }

  &__file {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-3) var(--v2-space-4);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-muted);
  }

  &__file-icon {
    color: var(--v2-success);
  }

  &__remove-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-destructive);
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(229, 72, 77, 0.1);
    }
  }
}
</style>
